import type { JobOrderUpdate } from "./jobOrdering";

/**
 * Row-key vocabulary + save-time planning for mixed job/dropoff-stop sequences.
 *
 * A shift's staged run order is a list of row keys — `job:<jobId>`, `stop:<shiftDropoffId>`
 * (a committed junction record), or `newstop:<tempKey>` (a stop staged in the UI, created on
 * save). The generic string-list operations (insert/move/reconcile in `jobOrdering.ts`)
 * work on these unchanged; the functions here derive the minimal Airtable writes from the
 * final sequences, mirroring MC's save loop (SchedulingMap.tsx:823-920).
 */

export type RowKey = string;
export type ParsedRowKey = { kind: "job" | "stop" | "newstop"; id: string };

export function jobRowKey(jobId: string): RowKey {
	return `job:${jobId}`;
}
export function stopRowKey(stopId: string): RowKey {
	return `stop:${stopId}`;
}
export function newStopRowKey(tempKey: string): RowKey {
	return `newstop:${tempKey}`;
}

/** Unprefixed strings parse as job ids so legacy jobId lists remain valid row-key lists. */
export function parseRowKey(key: RowKey): ParsedRowKey {
	if (key.startsWith("job:")) return { kind: "job", id: key.slice(4) };
	if (key.startsWith("stop:")) return { kind: "stop", id: key.slice(5) };
	if (key.startsWith("newstop:")) return { kind: "newstop", id: key.slice(8) };
	return { kind: "job", id: key };
}

/** The job ids of a row-key list, in sequence order. */
export function jobIdsOfRowKeys(keys: readonly RowKey[]): string[] {
	const ids: string[] = [];
	for (const key of keys) {
		const parsed = parseRowKey(key);
		if (parsed.kind === "job") ids.push(parsed.id);
	}
	return ids;
}

/**
 * Interleave dropoff stops into a job sequence at the row-key level, by MC's mixed-order rule:
 * each stop slots before the first job whose known order exceeds the stop's (an order tie keeps
 * the job first; a job with no known order counts as last), and stops without an order append in
 * input order. Shared by `buildShiftRows` (display) and the save path's fallback for a shift
 * whose mixed row order was never staged — one rule, one definition.
 */
export function interleaveStopRowKeys(
	orderedJobIds: readonly string[],
	jobOrderById: ReadonlyMap<string, number | undefined>,
	stops: readonly { id: string; order?: number }[],
): RowKey[] {
	const rows: RowKey[] = orderedJobIds.map(jobRowKey);
	const sortedStops = [...stops].sort((a, b) => {
		if (a.order === undefined) return b.order === undefined ? 0 : 1;
		if (b.order === undefined) return -1;
		return a.order - b.order;
	});
	for (const stop of sortedStops) {
		if (stop.order === undefined) {
			rows.push(stopRowKey(stop.id));
			continue;
		}
		// Ascending processing: lower-order stops are already placed, so scanning for the first
		// out-ordered JOB naturally lands this stop after them.
		const index = rows.findIndex((key) => {
			const parsed = parseRowKey(key);
			if (parsed.kind !== "job") return false;
			const jobOrder = jobOrderById.get(parsed.id);
			return jobOrder === undefined || jobOrder > stop.order!;
		});
		if (index === -1) rows.push(stopRowKey(stop.id));
		else rows.splice(index, 0, stopRowKey(stop.id));
	}
	return rows;
}

export interface StopOrderUpdate {
	stopId: string;
	order: number;
}

export interface MixedOrderUpdates {
	jobOrders: JobOrderUpdate[];
	stopOrders: StopOrderUpdate[];
	/** Orders for stops that don't exist yet — applied to the create payloads by tempKey. */
	newStopOrders: Map<string, number>;
}

/**
 * Minimal `Order` writes for the final mixed sequences: every row's order is its 0-based
 * index in its shift's mixed list (jobs' orders skip stop slots — exactly MC's loop index).
 * Jobs in `netRemovedJobIds` that appear in no list get their order cleared.
 */
export function computeMixedOrderUpdates(
	rowKeyLists: readonly (readonly RowKey[])[],
	netRemovedJobIds: Iterable<string>,
	knownJobOrders: ReadonlyMap<string, number | undefined>,
	knownStopOrders: ReadonlyMap<string, number | undefined>,
): MixedOrderUpdates {
	const jobOrders: JobOrderUpdate[] = [];
	const stopOrders: StopOrderUpdate[] = [];
	const newStopOrders = new Map<string, number>();
	const assignedJobs = new Set<string>();
	for (const list of rowKeyLists) {
		list.forEach((key, index) => {
			const { kind, id } = parseRowKey(key);
			if (kind === "job") {
				assignedJobs.add(id);
				if (knownJobOrders.get(id) !== index) jobOrders.push({ jobId: id, order: index });
			} else if (kind === "stop") {
				if (knownStopOrders.get(id) !== index) stopOrders.push({ stopId: id, order: index });
			} else {
				newStopOrders.set(id, index);
			}
		});
	}
	for (const jobId of netRemovedJobIds) {
		if (!assignedJobs.has(jobId)) jobOrders.push({ jobId, order: null });
	}
	return { jobOrders, stopOrders, newStopOrders };
}

export interface StopMembershipPlan {
	creates: { tempKey: string; dropoffId: string; shiftId: string }[];
	relinks: { stopId: string; shiftId: string }[];
	deletes: string[];
}

/**
 * Junction-record membership changes from the final sequences: staged new stops become
 * creates on their shift; a committed stop found under a different shift is relinked;
 * removal-staged stops are DELETED (deliberate deviation from MC, which orphans them with
 * `Shift=[]`) — their struck-through rows may still sit in the sequences until save.
 */
export function computeStopMembershipPlan(
	committedStopsByShiftId: ReadonlyMap<string, readonly string[]>,
	finalRowKeysByShiftId: ReadonlyMap<string, readonly RowKey[]>,
	newStopMetaByKey: ReadonlyMap<string, { dropoffId: string }>,
	removedStopIds: ReadonlySet<string>,
): StopMembershipPlan {
	const committedShiftByStopId = new Map<string, string>();
	for (const [shiftId, stopIds] of committedStopsByShiftId) {
		for (const stopId of stopIds) committedShiftByStopId.set(stopId, shiftId);
	}
	const creates: StopMembershipPlan["creates"] = [];
	const relinks: StopMembershipPlan["relinks"] = [];
	for (const [shiftId, keys] of finalRowKeysByShiftId) {
		for (const key of keys) {
			const { kind, id } = parseRowKey(key);
			if (kind === "newstop") {
				const meta = newStopMetaByKey.get(id);
				if (meta) creates.push({ tempKey: id, dropoffId: meta.dropoffId, shiftId });
			} else if (kind === "stop" && !removedStopIds.has(id)) {
				const committedShift = committedShiftByStopId.get(id);
				if (committedShift !== undefined && committedShift !== shiftId) relinks.push({ stopId: id, shiftId });
			}
		}
	}
	return { creates, relinks, deletes: [...removedStopIds] };
}

export interface JobDropoffLinkUpdate {
	jobId: string;
	shiftDropoffId: string | null;
}

/**
 * MC's sample-routing accumulator (SchedulingMap.tsx:874-889): walking each final sequence,
 * jobs accumulate until a stop is reached — those jobs' samples route to that stop; the
 * accumulator then resets. Jobs trailing the last stop are left untouched (MC never flushes),
 * and writes matching the job's known link are skipped. Row ids here must be RESOLVED — the
 * caller substitutes created junction ids for `newstop` temp keys before calling.
 */
export function computeJobDropoffLinks(
	sequences: readonly (readonly { kind: "job" | "stop"; id: string }[])[],
	knownLinkByJobId: ReadonlyMap<string, string | undefined>,
): JobDropoffLinkUpdate[] {
	const updates: JobDropoffLinkUpdate[] = [];
	for (const rows of sequences) {
		let accumulated: string[] = [];
		for (const row of rows) {
			if (row.kind === "job") {
				accumulated.push(row.id);
				continue;
			}
			for (const jobId of accumulated) {
				if (knownLinkByJobId.get(jobId) !== row.id) updates.push({ jobId, shiftDropoffId: row.id });
			}
			accumulated = [];
		}
	}
	return updates;
}
